import React, { useLayoutEffect } from 'react';

const isInViewport = (element) => {
  const rect = element.getBoundingClientRect();
  return rect.bottom > 0 && rect.right > 0 && rect.top < window.innerHeight && rect.left < window.innerWidth;
};

const isRevealDisabled = (element) => {
  if (!(element instanceof Element)) return false;
  if (element.getAttribute('data-scroll-reveal') === 'off') return true;
  return Boolean(element.closest('[data-scroll-reveal="off"]'));
};

const ScrollRevealImages = () => {
  useLayoutEffect(() => {
    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (prefersReducedMotion) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );

    const register = (element) => {
      if (!(element instanceof Element)) return;
      if (isRevealDisabled(element)) return;
      if (element.dataset.scrollRevealProcessed === '1') return;
      element.dataset.scrollRevealProcessed = '1';
      element.classList.add('scroll-reveal');
      if (isInViewport(element)) {
        element.classList.add('is-visible');
        return;
      }
      observer.observe(element);
    };

    const registerImage = (img) => {
      if (!(img instanceof HTMLImageElement)) return;
      register(img);
    };

    const registerText = (element) => {
      if (!(element instanceof Element)) return;
      if (element.getAttribute('data-scroll-reveal') !== 'on') return;
      register(element);
    };

    const scan = (root) => {
      if (!root) return;
      if (root instanceof HTMLImageElement) {
        registerImage(root);
        return;
      }
      if (!(root instanceof Element || root instanceof Document || root instanceof DocumentFragment)) return;
      root.querySelectorAll?.('img')?.forEach((img) => registerImage(img));
      root.querySelectorAll?.('[data-scroll-reveal="on"]')?.forEach((el) => registerText(el));
    };

    scan(document);

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => scan(node));
      });
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  return null;
};

export default ScrollRevealImages;
